import { DialogClose } from "@radix-ui/react-dialog";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import {
  ApiError,
  Auth0AccountRole,
  InvitesService,
  FormSummary,
  InviteType,
  ParentLimitedResponse,
  ParentsService,
  StudentsService,
  TutorStudentsService,
} from "client/openapi";
import { DialogAction, DialogActions, DialogContent } from "components/Dialog";
import { useContext, useEffect, useState } from "react";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import "./index.css";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { ButtonColor } from "components/Button";
import { Select } from "components/Select";
import { concatenateName } from "util/concatenateName";
import { RecentKind } from "client/openapi/models/RecentKind";
import { recentKey, useRecentOptions } from "util/recentPeople";
import { TutorContext } from "../TutorDashboard/tutorContext";
import SendFormControls, {
  FormChoiceState,
  NO_FORM,
} from "components/Forms/SendFormControls";
import { sendChosenForm } from "components/Forms/formsApi";
import SendTemplateControls, {
  NO_TEMPLATE,
  TemplateChoiceState,
} from "components/MessageTemplates/SendTemplateControls";
import { sendChosenTemplate } from "components/MessageTemplates/templatesApi";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";

const emptyStudent = {
  email: "",
  first_name: "",
  last_name: "",
};

function InviteStudentDialog({
  fetchStudents,
  currentPage,
  orgId,
  initialEmail = "",
  initialPhone = "",
}: {
  fetchStudents: (page: number) => Promise<void>;
  currentPage: number;
  orgId: number;
  /** Filled in (an unknown sender being added from Communication). */
  initialEmail?: string;
  initialPhone?: string;
}) {
  const { account, currently_selected_role } = useContext(
    OrgRolesAndAccountContext
  );
  const { available_tutors, update_tutor_state } = useContext(TutorContext);

  const [newStudent, setNewStudent] = useState({
    ...emptyStudent,
    email: initialEmail,
  });
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();
  const [parentOptions, setParentOptions] = useState<ParentLimitedResponse[]>();
  const [parent, setParent] = useState<ParentLimitedResponse>();
  const pickableParents = useRecentOptions([RecentKind.PARENT]);
  const [isEmailRequired, setIsEmailRequired] = useState(false);

  const [createAccount, setCreateAccount] = useState(false);
  const [formChoice, setFormChoice] = useState<FormChoiceState>(NO_FORM);
  const [templateChoice, setTemplateChoice] =
    useState<TemplateChoiceState>(NO_TEMPLATE);

  const isInviteValid = () => {
    return (
      newStudent.first_name.trim() !== "" &&
      newStudent.last_name.trim() !== "" &&
      (newStudent.email.trim() === "" ||
        (newStudent.email.includes("@") && newStudent.email.includes("."))) &&
      (phoneNumber === "" || isPhoneValid)
    );
  };

  function getNewestParents() {
    ParentsService.getParentsByOrganization({ orgId: orgId })
      .then((parents) => {
        setParentOptions(parents);
      })
      .catch((e: ApiError) => {
        setError(e);
      });
  }

  useEffect(() => {
    setIsEmailRequired(createAccount);
  }, [createAccount]);

  async function inviteStudent() {
    setStatus(PageStatus.LOADING);

    try {
      let invite_response;

      invite_response = await InvitesService.inviteUser({
        requestBody: {
          first_name: newStudent.first_name,
          last_name: newStudent.last_name,
          phone_number: phoneNumber || undefined,
          email: newStudent.email || undefined,
          org_invited_by: orgId,
          acct_invited_by: account?.reference_id as string,
          type: InviteType.STUDENT,
        },
        role: Auth0AccountRole.ME,
        createEntity: true,
        createAccount: createAccount,
      });

      setStatus(PageStatus.SUCCESS);
      Notifications.success(`Student invited!`);
      fetchStudents(currentPage);

      if (parent) {
        await ParentsService.addExistingStudentToParent({
          parentId: parent.id,
          studentId: invite_response[1].id,
        });
      }

      if (
        available_tutors &&
        currently_selected_role === Auth0AccountRole.ORG_TUTOR
      ) {
        const students_tutor = available_tutors.find(
          (tutor) => tutor.org_id === orgId
        );

        if (students_tutor) {
          await TutorStudentsService.createTutorStudentRelationship({
            tutorId: students_tutor.id,
            studentId: invite_response[1].id,
          });

          update_tutor_state({ need_to_get_latest_tutor_info: true });
        }
      }

      // Sent after the parent link, so the parent receives it too.
      sendChosenForm({
        orgId,
        ...formChoice,
        personType: Auth0AccountRole.ME,
        personId: invite_response?.[1]?.id,
      });
      sendChosenTemplate({
        ...templateChoice,
        personType: Auth0AccountRole.ME,
        personId: invite_response?.[1]?.id,
      });
    } catch (e: any) {
      setStatus(PageStatus.ERROR);
      setError(e);
    }
  }

  function handleUpdate(ev) {
    const value = ev.target.value;

    setNewStudent({
      ...newStudent,
      [ev.target.name]: value,
    });
  }

  function handleSubmit() {
    return async (ev) => {
      ev.preventDefault();
      await inviteStudent();
    };
  }

  function clearForm() {
    setNewStudent(emptyStudent);
    setStatus(undefined);
    setParent(undefined);
    setCreateAccount(false);
    setFormChoice(NO_FORM);
    setTemplateChoice(NO_TEMPLATE);
  }

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <>
      <DialogContent
        className="dialog-content"
        onOpen={getNewestParents}
        onClose={clearForm}
      >
        <div className="invite-dialog">
          <h2 className="invite-dialog--title">Add a Student</h2>

          {status === PageStatus.SUCCESS ? (
            <>
              <p className="mb-4">
                <b>
                  {newStudent.first_name} {newStudent.last_name}
                </b>{" "}
                has been successfully invited!
              </p>

              <DialogActions>
                <DialogAction
                  primary={true}
                  color={ButtonColor.SKYBLUE}
                  onClick={clearForm}
                >
                  Add Another <ArrowRightIcon />
                </DialogAction>

                <DialogClose asChild>
                  <DialogAction color={ButtonColor.SKYBLUE} onClick={clearForm}>
                    Close
                  </DialogAction>
                </DialogClose>
              </DialogActions>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit()}>
                <label htmlFor="first_name" className="input-label">
                  First Name
                  <input
                    id="first_name"
                    name="first_name"
                    type="text"
                    className="input"
                    placeholder="Amy"
                    value={newStudent.first_name}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required
                  />
                </label>

                <label htmlFor="last_name" className="mt-4 input-label">
                  Last Name
                  <input
                    id="last_name"
                    name="last_name"
                    type="text"
                    className="input"
                    placeholder="Smith"
                    value={newStudent.last_name}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required
                  />
                </label>

                <label htmlFor="add-parent" className="mt-4 input-label">
                  Parent (optional)
                  <Select
                    id="add-parent"
                    name="add-parent"
                    options={pickableParents.options(
                      parentOptions ?? [],
                      (option) => recentKey(RecentKind.PARENT, option.id)
                    )}
                    onInputChange={pickableParents.onInputChange}
                    value={parent}
                    isDisabled={parentOptions?.length === 0}
                    getOptionLabel={(p) => `${p ? concatenateName(p) : ""}`}
                    getOptionValue={(p) => (p ? p.id.toString() : "")}
                    onChange={(parent: ParentLimitedResponse) =>
                      setParent(parent)
                    }
                    className="w-6/6 h-9 mt-2 text-sm"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        pointerEvents: "auto",
                      }),
                      option: (base) => ({
                        ...base,
                        cursor: "pointer",
                      }),
                    }}
                  />
                </label>

                <label htmlFor="email" className="mt-4 input-label">
                  Email Address (required if inviting to create an account)
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="input"
                    placeholder="amy@example.com"
                    value={newStudent.email}
                    onChange={handleUpdate}
                    disabled={status === PageStatus.LOADING}
                    required={isEmailRequired}
                  />
                </label>

                <label htmlFor="phone_number" className="mt-4 input-label">
                  Phone Number (optional)
                  <div className="mt-2">
                    <IntlTelInput
                      initialValue={initialPhone}
                      inputProps={{
                        className: "input mt-2",
                        name: "student_phone_number",
                      }}
                      onChangeValidity={setIsPhoneValid}
                      onChangeNumber={(number) => setPhoneNumber(number)}
                      initOptions={{
                        loadUtils: () =>
                          import("intl-tel-input/build/js/utils.js"),
                        strictMode: true,
                        autoPlaceholder: "aggressive",
                        placeholderNumberType: "MOBILE",
                        countryOrder: ["us", "gb", "ca"],
                        initialCountry: "auto",
                        geoIpLookup: (success, failure) => {
                          fetch("https://ipapi.co/json/")
                            .then((res) => res.json())
                            .then((data) => success(data.country_code))
                            .catch(() => failure());
                        },
                        separateDialCode: true,
                        nationalMode: true,
                      }}
                    />
                  </div>
                </label>

                <SendFormControls
                  orgId={orgId}
                  audience={FormSummary.audience.FAMILIES}
                  value={formChoice}
                  onChange={setFormChoice}
                  disabled={status === PageStatus.LOADING}
                />
                <SendTemplateControls
                  orgId={orgId}
                  value={templateChoice}
                  onChange={setTemplateChoice}
                  disabled={status === PageStatus.LOADING}
                />

                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="create-account"
                    checked={createAccount}
                    onChange={() => setCreateAccount(!createAccount)}
                    disabled={status === PageStatus.LOADING}
                    className="mr-2"
                  />
                  <label htmlFor="create-account">
                    Invite user to create an account
                  </label>
                </div>

                <DialogActions>
                  <DialogAction
                    primary={true}
                    color={
                      !isInviteValid() ? ButtonColor.GRAY : ButtonColor.SKYBLUE
                    }
                    type="submit"
                    disabled={status === PageStatus.LOADING || !isInviteValid()}
                  >
                    {status === PageStatus.LOADING ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : (
                      <>
                        Add <ArrowRightIcon />
                      </>
                    )}
                  </DialogAction>

                  <DialogClose asChild>
                    <DialogAction
                      color={ButtonColor.SKYBLUE}
                      onClick={clearForm}
                    >
                      Cancel
                    </DialogAction>
                  </DialogClose>
                </DialogActions>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </>
  );
}

export default InviteStudentDialog;
